import {
  Component, inject, signal, ViewChild, ElementRef,
  AfterViewChecked, computed, effect, HostListener,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { marked } from 'marked';
import { httpsCallable } from 'firebase/functions';
import {
  collection, deleteDoc, doc, onSnapshot, Unsubscribe,
} from 'firebase/firestore';
import { FirebaseService } from '../../core/services/firebase.service';
import { DataStoreService } from '../../core/services/data-store.service';
import { OrgContextService } from '../../core/services/org-context.service';
import { SupportChatbotService } from './support-chatbot.service';
import { environment } from '../../../environments/environment';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}


@Component({
  selector: 'app-support-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule],
  template: `
    <ng-container *transloco="let t">
    @if (authed()) {
      @if (open()) {
        <div class="fixed bottom-5 right-5 z-50 w-[22rem] max-w-[calc(100vw-2rem)] h-[28rem] max-h-[calc(100vh-6rem)] rounded-xl shadow-2xl border border-border bg-background flex flex-col overflow-hidden">
          <div class="flex items-center justify-between px-4 py-3 border-b border-border">
            <div class="font-semibold text-sm text-text">{{ t('support-chatbot.title') }}</div>
            <div class="flex items-center gap-1">
              @if (sending()) {
                <button type="button" (click)="cancel()" [attr.aria-label]="t('support-chatbot.stop')"
                        class="px-2 py-1 text-xs rounded hover:bg-surface text-text-secondary cursor-pointer">
                  {{ t('support-chatbot.stop') }}
                </button>
              }
              <button type="button" (click)="toggle()" [attr.aria-label]="t('support-chatbot.close')"
                      class="p-1 rounded hover:bg-surface text-text-secondary cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div #scroll class="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
            @if (messages().length === 0 && hasOrg() && hasCredits()) {
              <div class="text-text-secondary text-center mt-8">{{ t('support-chatbot.empty-state') }}</div>
            } @else if (messages().length === 0 && hasOrg() && !hasCredits()) {
              <div class="flex flex-col items-center justify-center min-h-full text-center px-4 space-y-4">
                <p class="text-text-secondary">{{ t('support-chatbot.no-credits') }}</p>
                <button type="button" (click)="goToBilling()"
                        class="inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors cursor-pointer">
                  {{ t('support-chatbot.buy-credits') }}
                </button>
              </div>
            }
            @for (msg of messages(); track $index) {
              @if (msg.role === 'user') {
                <div class="flex justify-end">
                  <div class="inline-block max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words bg-surface text-text">
                    {{ msg.content }}
                  </div>
                </div>
              } @else {
                <div class="text-sm leading-relaxed text-text chat-markdown">
                  @if (msg.content) {
                    <div [innerHTML]="renderMarkdown(msg.content)"></div>
                  } @else if (sending()) {
                    <span class="sc-typing" aria-hidden="true">
                      <span class="sc-typing-dot"></span>
                      <span class="sc-typing-dot"></span>
                      <span class="sc-typing-dot"></span>
                    </span>
                  }
                </div>
              }
            }
            @if (errorMsg()) {
              <div class="text-xs text-red-500">{{ errorMsg() }}</div>
            }
          </div>

          <div class="p-2">
            <div class="flex items-end gap-2 w-full rounded-lg border border-border px-2 py-1.5 transition-colors hover:border-text-secondary/40 focus-within:border-text-secondary/40"
                 style="background-color: color-mix(in srgb, var(--surface-color) 50%, transparent); backdrop-filter: blur(4px);">
              <textarea
                #ta
                [(ngModel)]="input"
                (keydown)="onKeydown($event)"
                (input)="autoGrow(ta)"
                [disabled]="sending() || !hasCredits()"
                rows="1"
                [placeholder]="t('support-chatbot.placeholder')"
                class="flex-1 resize-none bg-transparent text-sm text-text placeholder:text-text-secondary/60 outline-none py-1 max-h-40 overflow-auto leading-snug"
              ></textarea>
              <button
                type="button"
                (click)="send(ta)"
                [disabled]="sending() || !input.trim() || !hasCredits()"
                aria-label="Send"
                class="shrink-0 rounded-full p-1.5 transition-colors bg-primary-600 text-white hover:bg-primary-700 cursor-pointer disabled:bg-border disabled:text-text-secondary disabled:cursor-default inline-flex items-center justify-center size-8"
              >
                @if (sending()) {
                  <span class="sc-spinner" aria-hidden="true"></span>
                } @else {
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
                    <path fill-rule="evenodd" d="M10 17a.75.75 0 0 1-.75-.75V5.56L5.22 9.59a.75.75 0 1 1-1.04-1.08l5.25-5.25a.75.75 0 0 1 1.04 0l5.25 5.25a.75.75 0 1 1-1.04 1.08L10.75 5.56v10.69A.75.75 0 0 1 10 17Z" clip-rule="evenodd" />
                  </svg>
                }
              </button>
            </div>
          </div>
        </div>
      }
    }
    </ng-container>
  `,
  styles: [`
    .chat-markdown :first-child { margin-top: 0; }
    .chat-markdown :last-child { margin-bottom: 0; }
    .chat-markdown p { margin: 0.5rem 0; }
    .chat-markdown h1, .chat-markdown h2, .chat-markdown h3,
    .chat-markdown h4, .chat-markdown h5, .chat-markdown h6 {
      font-weight: 600; margin: 0.85rem 0 0.35rem; line-height: 1.3;
    }
    .chat-markdown h1 { font-size: 1.1rem; }
    .chat-markdown h2 { font-size: 1.02rem; }
    .chat-markdown h3 { font-size: 0.95rem; }
    .chat-markdown h4, .chat-markdown h5, .chat-markdown h6 { font-size: 0.9rem; }
    .chat-markdown ul, .chat-markdown ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }
    .chat-markdown ul { list-style: disc outside; }
    .chat-markdown ol { list-style: decimal outside; }
    .chat-markdown ul ul { list-style: circle outside; }
    .chat-markdown ul ul ul { list-style: square outside; }
    .chat-markdown ul ul, .chat-markdown ol ol,
    .chat-markdown ul ol, .chat-markdown ol ul {
      margin: 0.25rem 0;
    }
    .chat-markdown li { margin: 0.2rem 0; padding-left: 0.15rem; }
    .chat-markdown li > p { margin: 0.2rem 0; }
    .chat-markdown li::marker { color: var(--text-secondary-color, #6b7280); }
    .chat-markdown a { color: #4c17ca; text-decoration: underline; text-underline-offset: 2px; }
    .chat-markdown a:hover { text-decoration-thickness: 2px; }
    .chat-markdown strong { font-weight: 600; }
    .chat-markdown em { font-style: italic; }
    .chat-markdown code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.85em;
      background: rgba(127, 127, 127, 0.18);
      padding: 0.1em 0.35em;
      border-radius: 0.25rem;
    }
    .chat-markdown pre {
      background: rgba(127, 127, 127, 0.15);
      padding: 0.65rem 0.75rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      margin: 0.6rem 0;
      font-size: 0.85em;
      line-height: 1.5;
    }
    .chat-markdown pre code {
      background: transparent;
      padding: 0;
      font-size: 1em;
    }
    .chat-markdown blockquote {
      border-left: 3px solid rgba(127, 127, 127, 0.4);
      padding: 0.1rem 0 0.1rem 0.65rem;
      margin: 0.5rem 0;
      color: var(--text-secondary-color, #6b7280);
    }
    .chat-markdown hr { border: none; border-top: 1px solid rgba(127, 127, 127, 0.3); margin: 0.75rem 0; }
    .chat-markdown table {
      border-collapse: collapse;
      margin: 0.5rem 0;
      font-size: 0.9em;
      width: 100%;
    }
    .chat-markdown th, .chat-markdown td {
      border: 1px solid rgba(127, 127, 127, 0.3);
      padding: 0.3rem 0.5rem;
      text-align: left;
    }
    .chat-markdown th {
      background: rgba(127, 127, 127, 0.1);
      font-weight: 600;
    }

    .sc-spinner {
      width: 1rem; height: 1rem; border-radius: 9999px;
      border: 2px solid currentColor; border-top-color: transparent;
      animation: sc-spin 1s linear infinite;
    }
    @keyframes sc-spin { to { transform: rotate(360deg); } }

    .sc-typing { display: inline-flex; align-items: center; gap: 4px; vertical-align: middle; }
    .sc-typing-dot {
      display: inline-block; width: 6px; height: 6px; border-radius: 9999px;
      background-color: var(--text-secondary-color, #6b7280);
      animation: sc-typing 1.4s infinite;
    }
    .sc-typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .sc-typing-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes sc-typing {
      0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
      30%           { opacity: 1;   transform: translateY(-2px); }
    }
  `],
  encapsulation: ViewEncapsulation.None,
})
export class SupportChatbotComponent implements AfterViewChecked {
  private firebase = inject(FirebaseService);
  private dataStore = inject(DataStoreService);
  private orgContext = inject(OrgContextService);
  private supportChatbot = inject(SupportChatbotService);
  private router = inject(Router);
  private transloco = inject(TranslocoService);

  @ViewChild('scroll') scroll?: ElementRef<HTMLDivElement>;
  @ViewChild('panel') panel?: ElementRef<HTMLDivElement>;

  readonly open = this.supportChatbot.open;
  readonly messages = signal<ChatMessage[]>([]);
  readonly sending = signal(false);
  readonly errorMsg = signal('');
  readonly authed = computed(() => !!this.dataStore.authUser());
  readonly hasOrg = computed(() => !!this.orgContext.currentOrg());
  readonly hasCredits = computed(() => (this.orgContext.currentOrg()?.creditBalance ?? 0) > 0);
  readonly panelPos = signal<{ x: number; y: number } | null>(this.loadSavedPosition());
  readonly dragging = signal(false);

  private dragPointerId: number | null = null;
  private dragOffset = { x: 0, y: 0 };
  private static readonly POS_KEY = 'support-chatbot-pos-v1';

  input = '';
  private shouldScroll = false;
  private sessionId: string | null = null;
  private currentRunId: string | null = null;
  private abortController: AbortController | null = null;
  private pendingActionsUnsub: Unsubscribe | null = null;
  private readonly dispatchedActionIds = new Set<string>();
  private lastAuthUid: string | undefined = this.dataStore.authUser()?.uid ?? undefined;

  constructor() {
    // New sessionId every time the panel opens; drop transcript when closed.
    // The pendingActions subscription is attached lazily on 'run-started',
    // since its Firestore rule requires the parent session doc (written
    // server-side on the first request) to exist first.
    effect(() => {
      if (this.open()) {
        if (!this.sessionId) this.sessionId = this.generateSessionId();
      } else {
        this.resetSession();
      }
    });

    // When the signed-in user changes identity (logout, account switch) the
    // previous transcript must not leak into the next session. Close the
    // panel and wipe local state.
    effect(() => {
      const uid = this.dataStore.authUser()?.uid ?? undefined;
      if (uid !== this.lastAuthUid) {
        this.lastAuthUid = uid;
        if (this.supportChatbot.open()) this.supportChatbot.open.set(false);
        this.resetSession();
      }
    });
  }

  toggle() {
    this.supportChatbot.toggle();
    this.shouldScroll = true;
  }

  goToBilling() {
    this.supportChatbot.open.set(false);
    // Billing lives in the Admin console, not in Create.
    window.open(`${environment.adminConsoleUrl}/billing`, '_blank', 'noopener');
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  private resetSession() {
    this.abortController?.abort();
    this.abortController = null;
    this.pendingActionsUnsub?.();
    this.pendingActionsUnsub = null;
    this.dispatchedActionIds.clear();
    this.sessionId = null;
    this.currentRunId = null;
    this.messages.set([]);
    this.errorMsg.set('');
    this.sending.set(false);
  }

  /**
   * Listen for server-side tool outputs that need to drive the UI
   * (navigation with optional intent, etc.). Each action doc is dispatched
   * once and deleted.
   */
  private subscribePendingActions(sessionId: string) {
    if (this.pendingActionsUnsub) return;
    const ref = collection(this.firebase.firestore, `supportSessions/${sessionId}/pendingActions`);
    this.pendingActionsUnsub = onSnapshot(
      ref,
      (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type !== 'added') continue;
          const docId = change.doc.id;
          if (this.dispatchedActionIds.has(docId)) continue;
          this.dispatchedActionIds.add(docId);
          const data = change.doc.data() as { action?: string; params?: Record<string, unknown> };
          void this.runPendingAction(sessionId, docId, data.action, data.params ?? {});
        }
      },
      (err) => {
        console.warn('[support-chatbot] pendingActions subscription error:', err);
        this.errorMsg.set(this.transloco.translate('support-chatbot.generic-error'));
      },
    );
  }

  private async runPendingAction(
    sessionId: string,
    docId: string,
    action: string | undefined,
    params: Record<string, unknown>,
  ): Promise<void> {
    try {
      await this.dispatchPendingAction(action, params);
    } catch (err) {
      console.warn('[support-chatbot] failed to dispatch pending action:', action, err);
      this.errorMsg.set(this.transloco.translate('support-chatbot.generic-error'));
      // Leave the allow-list entry in place so we don't retry a known-bad
      // action on every reconnect, but delete the doc so it doesn't pile up.
    }
    try {
      await deleteDoc(doc(this.firebase.firestore,
        `supportSessions/${sessionId}/pendingActions/${docId}`));
    } catch (err) {
      console.warn('[support-chatbot] failed to delete dispatched action doc:', err);
    }
  }

  private async dispatchPendingAction(action: string | undefined, params: Record<string, unknown>): Promise<void> {
    if (action === 'navigate') {
      const route = typeof params['route'] === 'string' ? (params['route'] as string) : null;
      const intent = typeof params['intent'] === 'string' ? (params['intent'] as string) : undefined;
      if (!route) throw new Error('navigate action missing route');
      const extras = intent ? { queryParams: { action: intent } } : undefined;
      const ok = await this.router.navigateByUrl(
        this.router.createUrlTree([route], extras).toString(),
      );
      // If the router refuses (typically because a route guard redirected the
      // user elsewhere — e.g. they lack the page's read-permission), treat it
      // as an expected outcome rather than a crash. The agent is taught to
      // refuse these upfront; if one slips through, the guard's redirect is
      // the product's correct behavior, and surfacing "Something went wrong"
      // on top of it scares the user for no reason.
      if (!ok) {
        console.warn('[support-chatbot] router refused navigation to', route, '— guard likely redirected.');
      }
      return;
    }
    throw new Error(`unknown pending action: ${action}`);
  }

  ngAfterViewChecked() {
    if (this.shouldScroll && this.scroll) {
      this.scroll.nativeElement.scrollTop = this.scroll.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  onHeaderPointerDown(event: PointerEvent) {
    // Don't start a drag when the user clicks the close/stop buttons inside the header.
    if ((event.target as HTMLElement).closest('button')) return;
    const panel = this.panel?.nativeElement;
    if (!panel) return;
    event.preventDefault();
    const rect = panel.getBoundingClientRect();
    this.dragOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    this.dragPointerId = event.pointerId;
    // Snap to explicit coords immediately so subsequent moves don't jump from the default anchor.
    this.panelPos.set(this.clampPosition(rect.left, rect.top, rect.width, rect.height));
    this.dragging.set(true);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (!this.dragging() || event.pointerId !== this.dragPointerId) return;
    const panel = this.panel?.nativeElement;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const nextX = event.clientX - this.dragOffset.x;
    const nextY = event.clientY - this.dragOffset.y;
    this.panelPos.set(this.clampPosition(nextX, nextY, rect.width, rect.height));
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onPointerUp(event: PointerEvent) {
    if (event.pointerId !== this.dragPointerId) return;
    this.dragging.set(false);
    this.dragPointerId = null;
    this.savePosition(this.panelPos());
  }

  @HostListener('window:resize')
  onWindowResize() {
    const pos = this.panelPos();
    const panel = this.panel?.nativeElement;
    if (!pos || !panel) return;
    const rect = panel.getBoundingClientRect();
    this.panelPos.set(this.clampPosition(pos.x, pos.y, rect.width, rect.height));
  }

  private clampPosition(x: number, y: number, w: number, h: number): { x: number; y: number } {
    const maxX = Math.max(0, window.innerWidth - w);
    const maxY = Math.max(0, window.innerHeight - h);
    return { x: Math.min(Math.max(0, x), maxX), y: Math.min(Math.max(0, y), maxY) };
  }

  private loadSavedPosition(): { x: number; y: number } | null {
    try {
      const raw = localStorage.getItem(SupportChatbotComponent.POS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { x: number; y: number };
      if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return null;
      return parsed;
    } catch {
      return null;
    }
  }

  private savePosition(pos: { x: number; y: number } | null) {
    try {
      if (pos) localStorage.setItem(SupportChatbotComponent.POS_KEY, JSON.stringify(pos));
      else localStorage.removeItem(SupportChatbotComponent.POS_KEY);
    } catch {
      // quota exceeded / disabled — non-fatal
    }
  }

  renderMarkdown(text: string): string {
    return marked.parse(text, { async: false, breaks: true, gfm: true }) as string;
  }

  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  async cancel() {
    if (!this.sessionId || !this.currentRunId) {
      this.abortController?.abort();
      return;
    }
    try {
      const call = httpsCallable<
        { sessionId: string; runId: string },
        { cancelled: boolean; status: string }
      >(this.firebase.functions, 'cancelSupportRun');
      await call({ sessionId: this.sessionId, runId: this.currentRunId });
    } catch {
      // Best-effort — the abort controller also trips the local stream.
    }
    this.abortController?.abort();
  }

  autoGrow(ta: HTMLTextAreaElement) {
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }

  async send(ta?: HTMLTextAreaElement) {
    const text = this.input.trim();
    if (!text || this.sending() || !this.hasCredits()) return;
    if (!this.sessionId) this.sessionId = this.generateSessionId();

    this.errorMsg.set('');
    const next: ChatMessage[] = [
      ...this.messages(),
      { role: 'user', content: text },
      { role: 'assistant', content: '' },
    ];
    this.messages.set(next);
    this.input = '';
    if (ta) ta.style.height = '';
    this.sending.set(true);
    this.shouldScroll = true;

    try {
      await this.stream(text);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        this.errorMsg.set(err?.message || this.transloco.translate('support-chatbot.generic-error'));
      }
    } finally {
      this.sending.set(false);
      this.currentRunId = null;
      this.abortController = null;
      this.shouldScroll = true;
    }
  }

  private async stream(message: string): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) {
      this.errorMsg.set(this.transloco.translate('support-chatbot.not-authenticated'));
      return;
    }
    const idToken = await user.getIdToken();
    const endpoint = this.endpointUrl();

    this.abortController = new AbortController();
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        sessionId: this.sessionId,
        message,
        app: 'reach',
        orgId: this.orgContext.currentOrg()?.id,
      }),
      signal: this.abortController.signal,
    });

    if (resp.status === 402) {
      throw new Error(this.transloco.translate('support-chatbot.no-credits'));
    }
    if (!resp.ok || !resp.body) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Server ${resp.status}: ${body || resp.statusText}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            this.handleEvent(event);
          } catch {
            // Ignore malformed frame
          }
        }
      }
    }
  }

  private handleEvent(event: any): void {
    const type = event?.type as string | undefined;
    if (!type) return;

    if (type === 'run-started') {
      this.currentRunId = event.runId;
      if (this.sessionId) this.subscribePendingActions(this.sessionId);
      return;
    }

    if (type === 'token' && typeof event.token === 'string') {
      this.appendToLastAssistant(event.token);
      this.shouldScroll = true;
      return;
    }

    if (type === 'error' && typeof event.error === 'string') {
      const code = event.error;
      const knownKey = `support-chatbot.${code}`;
      const translated = this.transloco.translate(knownKey);
      this.errorMsg.set(translated !== knownKey ? translated : code);
      return;
    }

    // 'done', 'generation-status', etc. — no UI change required.
  }

  private appendToLastAssistant(chunk: string) {
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== 'assistant') {
      this.messages.set([...msgs, { role: 'assistant', content: chunk }]);
      return;
    }
    const updated: ChatMessage = { role: 'assistant', content: last.content + chunk };
    this.messages.set([...msgs.slice(0, -1), updated]);
  }

  // The active org's regional project + functions region (data residency).
  private endpointUrl(): string {
    return this.firebase.functionUrl('runSupportAgent');
  }
}
